import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { UnavailableState } from '../../../shared/dashboard/UnavailableState';
import { StatusBadge } from '../../../shared/dashboard/StatusBadge';
import { formatCurrency, type TaxAlert } from '../types';

function formatDueDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${month}/${day}/${year}`;
}

/** `destinationHref` is optional and defaults to `undefined` — every existing caller (the standalone
 * `/dashboard`) keeps today's plain, non-interactive row with no behavior change. Only a caller that
 * explicitly opts in (EPN-42's Admin Dashboard, which verified `/payroll-taxes` genuinely shows this same
 * unpaid-liability data with no extra steps) gets a real, keyboard-operable link on every row — never a
 * decorative chevron implying a destination that doesn't exist. */
function AlertRow({ alert, destinationHref }: { alert: TaxAlert; destinationHref?: string | undefined }) {
  const content: ReactNode = (
    <>
      <div>
        <p className="ed-alert-title">
          Taxes Due: {formatDueDate(alert.dueDate)}
          {alert.isOverdue && <> <StatusBadge kind="critical">Overdue</StatusBadge></>}
        </p>
        <p className="ed-alert-desc">Amount: {formatCurrency(alert.amount)} for {alert.formLabel}</p>
      </div>
      <span className="ed-alert-chevron" aria-hidden="true">›</span>
    </>
  );

  if (destinationHref) {
    return (
      <Link to={destinationHref} className="ed-alert-row" aria-label={`Taxes due ${formatDueDate(alert.dueDate)}${alert.isOverdue ? ' (overdue)' : ''}: ${formatCurrency(alert.amount)} for ${alert.formLabel}. View tax payments.`}>
        {content}
      </Link>
    );
  }

  return <div className="ed-alert-row">{content}</div>;
}

/** The "Most Recent" control is real here (unlike legacy's, confirmed decorative during investigation —
 * see docs/dashboard-architecture.md) — it's the only sort this data actually has, so it's rendered as an
 * inert label rather than a fake interactive dropdown with no other option behind it. */
export function AlertsPanel({ alerts, unavailable, destinationHref }: { alerts: TaxAlert[]; unavailable: boolean; destinationHref?: string | undefined }) {
  return (
    <section className="ed-section-card">
      <div className="ed-section-header ed-alerts-header">
        <div className="ed-section-header-left">
          <span className="ed-section-icon ed-alerts-icon" aria-hidden="true">🔔</span>
          <h2 className="ed-section-title ed-alerts-title">Alerts and To-Dos</h2>
        </div>
        <span className="ed-sort-control">
          Soonest due first
        </span>
      </div>

      {unavailable && (
        <div className="ed-section-error">
          <UnavailableState reason="The tax service could not be reached, so tax alerts cannot be shown. Try again shortly." />
        </div>
      )}

      {!unavailable && alerts.length === 0 && (
        <div className="ed-empty-state">No alerts or to-dos right now.</div>
      )}

      {!unavailable && alerts.length > 0 && (
        <div className="ed-alerts-list">
          {alerts.map((alert, index) => (
            <AlertRow key={`${alert.formLabel}-${alert.dueDate}-${index}`} alert={alert} destinationHref={destinationHref} />
          ))}
        </div>
      )}
    </section>
  );
}
